import { styled, SxProps, Tab as MuiTab, Typography } from '@mui/material'
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'

function getTabsMeta(container: HTMLDivElement, tab: HTMLDivElement) {
  const containerRect = container.getBoundingClientRect()
  const containerMeta = {
    scrollLeft: container.scrollLeft,
    right: containerRect.right,
    left: containerRect.left,
  }

  const tabRect = tab.getBoundingClientRect()
  return { containerMeta, tabMeta: tabRect }
}

const TabsContainer = styled('div')`
  display: flex;
  height: 39px;
  background-color: ${({ theme }) => theme.palette.neutral000};

  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.palette.neutral300};
  position: relative;
  overflow: auto hidden;

  -ms-overflow-style: none /* ie and edge */;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Tab = styled(MuiTab)`
  &.MuiTab-root {
    border-bottom: none;
    flex-grow: 1;
  }
  min-width: 20%;
  border-radius: 8px 8px 0 0;
  gap: 8px;
  flex-direction: row-reverse;
`

const Indicator = styled('div')`
  transition:
    width 0.3s ease,
    right 0.3s ease,
    left 0.3s ease;
  position: absolute;
  width: 50px;
  right: 0;
  bottom: 0;
  height: 3px;
  background-color: ${({ theme }) => theme.palette.neutral900};
  transform: scale(0.7);
`

export interface ITab<T> {
  key?: string
  label: string
  value: T
  icon?: JSX.Element
  iconPosition?: 'end' | 'start'
  'aria-controls': string
  width?: string
}

interface Props<T> {
  tabs: ITab<T>[]
  value: T
  className?: string
  tabSx?: SxProps
  tabsSx?: SxProps
  indicatorStyles?: CSSProperties
  onChange: (newValue: T) => void
  isLtr?: boolean
}

export function Tabs<T extends string>({
  tabs,
  value,
  tabSx,
  tabsSx,
  className,
  indicatorStyles = {},
  onChange,
  isLtr,
}: Props<T>) {
  const tabsRef = useRef<HTMLDivElement[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const [indicatorState, setIndicatorState] = useState<CSSProperties>()

  // const theme = useTheme()
  // const isLtr = theme.direction === 'ltr'

  const updateIndicatorState = useCallback(
    (tab: HTMLDivElement) => {
      const container = containerRef.current
      if (!container) return
      const { containerMeta, tabMeta } = getTabsMeta(container, tab)
      const newIndicatorStyle: CSSProperties = {}
      newIndicatorStyle.width = tabMeta.width

      if (isLtr) {
        newIndicatorStyle.left =
          containerMeta.scrollLeft + tabMeta.left - containerMeta.left
      } else {
        newIndicatorStyle.right =
          -containerMeta.scrollLeft + containerMeta.right - tabMeta.right
      }
      setIndicatorState(newIndicatorStyle)
    },
    [isLtr],
  )

  const scrollSelectedTabIntoView = useCallback((tab: HTMLDivElement) => {
    const container = containerRef.current
    if (!container) return
    const { containerMeta, tabMeta } = getTabsMeta(container, tab)
    const rightDelta = tabMeta.right - containerMeta.right
    const leftDelta = containerMeta.left - tabMeta.left

    if (rightDelta > 0) {
      container.scrollTo({
        left: containerMeta.scrollLeft + rightDelta,
        behavior: 'smooth',
      })
    } else if (leftDelta > 0) {
      container.scrollTo({
        left: containerMeta.scrollLeft - leftDelta,
        behavior: 'smooth',
      })
    }
  }, [])

  const handleSelectTab = (val: T) => {
    onChange(val)
  }

  useEffect(() => {
    const tab = tabsRef.current.find(t => t.id === value)
    if (tab) {
      updateIndicatorState(tab)
      scrollSelectedTabIntoView(tab)
    }
  }, [value, scrollSelectedTabIntoView, updateIndicatorState, tabs])

  useEffect(() => {
    tabsRef.current = tabsRef.current.slice(0, tabs.length)
  }, [tabs])

  return (
    <TabsContainer ref={containerRef} className={className} sx={tabsSx}>
      {tabs.map((tab, i) => (
        <Tab
          ref={el => {
            if (el) tabsRef.current[i] = el
          }}
          sx={{ ...tabSx, width: tab.width }}
          icon={tab.icon}
          id={tab.value}
          value={tab.value}
          key={tab.key || tab.value}
          iconPosition={tab.iconPosition}
          data-selected={value === tab.value}
          label={
            <Typography
              color={value === tab.value ? 'neutral800' : 'neutral500'}
              variant="subtitle3"
            >
              {tab.label}
            </Typography>
          }
          aria-controls={tab['aria-controls']}
          onClick={() => {
            handleSelectTab(tab.value)
          }}
        />
      ))}

      <Indicator style={{ ...indicatorStyles, ...indicatorState }} />
    </TabsContainer>
  )
}
